// Shared visual primitives -mirror the mockup's mockups.jsx exactly.
// Server-component-safe (no hooks). Interactive primitives live in their
// own client modules (e.g. editable-field.tsx).

import type { CSSProperties, ReactNode } from 'react';
import { theme } from './theme';

// ────────────────────────────────────────────────────────────────────────────
// LocuslyMark -concentric "locus" pin + wordmark
// ────────────────────────────────────────────────────────────────────────────
export function LocuslyMark({
  color,
  size = 22,
}: {
  color?: string;
  size?: number;
}) {
  const c = color || theme.c.fg;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 9,
        color: c,
        fontFamily: theme.fonts.body,
        fontWeight: 600,
        fontSize: size,
        letterSpacing: -0.4,
        lineHeight: 1,
      }}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/locusly-google-profile.svg"
        alt=""
        aria-hidden="true"
        width={size * 1.1}
        height={size * 1.1}
        style={{ display: 'block' }}
      />
      <span>Locusly</span>
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// BrowserFrame -chrome window for showing a website preview
// ────────────────────────────────────────────────────────────────────────────
export function BrowserFrame({
  url = 'maria-bakery.pt',
  children,
  height,
  shadow = true,
  dark = false,
}: {
  url?: string;
  children: ReactNode;
  height?: number | string;
  shadow?: boolean;
  dark?: boolean;
}) {
  const bg = dark ? '#1C1C1A' : theme.c.surface;
  const fg = theme.c.fgMuted;
  const bar = dark ? '#262624' : theme.c.bgAlt;
  return (
    <div
      style={{
        background: bg,
        borderRadius: theme.radii.lg,
        border: `1px solid ${theme.c.border}`,
        overflow: 'hidden',
        boxShadow: shadow
          ? '0 30px 60px -30px rgba(0,0,0,0.25), 0 12px 24px -12px rgba(0,0,0,0.10)'
          : 'none',
        width: '100%',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 14px',
          background: bar,
          borderBottom: `1px solid ${theme.c.border}`,
        }}
      >
        <div style={{ display: 'flex', gap: 6 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 999,
              background: dark ? '#3a3a38' : '#D7D2C7',
            }}
          />
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 999,
              background: dark ? '#3a3a38' : '#D7D2C7',
            }}
          />
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 999,
              background: dark ? '#3a3a38' : '#D7D2C7',
            }}
          />
        </div>
        <div
          style={{
            flex: 1,
            height: 22,
            background: dark ? '#0F0F0E' : theme.c.bg,
            borderRadius: 6,
            border: `1px solid ${theme.c.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: theme.fonts.mono,
            fontSize: 11,
            color: fg,
          }}
        >
          <span style={{ opacity: 0.6, marginRight: 6 }} aria-hidden="true">
            🔒
          </span>
          {url}
        </div>
        <div style={{ width: 36 }} />
      </div>
      <div style={{ height }}>{children}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sparkline -small SVG line chart with gradient fill
// ────────────────────────────────────────────────────────────────────────────
export function Sparkline({
  values,
  color,
  height = 60,
  width = 240,
  fill = true,
  strokeWidth = 1.5,
  gradId,
  pathClassName,
}: {
  values: number[];
  color: string;
  height?: number;
  width?: number;
  fill?: boolean;
  strokeWidth?: number;
  gradId: string;
  pathClassName?: string;
}) {
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return [x, y] as const;
  });
  const d = pts
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(' ');
  const fillPath = `${d} L ${width},${height} L 0,${height} Z`;
  // SVG renders responsive: viewBox fixes the aspect ratio, width:100% lets
  // the chart shrink with its container. height:auto lets the browser keep
  // the viewBox ratio so the line never stretches.
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block', width: '100%', height: 'auto', maxHeight: height }}
      aria-hidden="true"
    >
      {fill && (
        <>
          <defs>
            <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.22" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={fillPath} fill={`url(#${gradId})`} />
        </>
      )}
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
        className={pathClassName}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// MiniBar -small horizontal bar list (top pages, top sources, etc.)
// ────────────────────────────────────────────────────────────────────────────
export function MiniBar({
  rows,
  color,
}: {
  rows: Array<{ k: string; v: number }>;
  color: string;
}) {
  const max = Math.max(...rows.map((r) => r.v));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      {rows.map((r, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontFamily: theme.fonts.mono,
            fontSize: 11,
            color: theme.c.fgMuted,
          }}
        >
          <div
            style={{
              flex: '0 0 110px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: theme.c.fg,
            }}
          >
            {r.k}
          </div>
          <div
            style={{
              flex: 1,
              height: 6,
              background: theme.c.chip,
              borderRadius: 999,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${(r.v / max) * 100}%`,
                height: '100%',
                background: color,
                borderRadius: 999,
              }}
            />
          </div>
          <div style={{ flex: '0 0 46px', textAlign: 'right' }}>{r.v.toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Donut -small doughnut chart for sources
// ────────────────────────────────────────────────────────────────────────────
export function Donut({
  segs,
  size = 110,
  thickness = 14,
  ringColor,
}: {
  segs: Array<{ v: number; color: string }>;
  size?: number;
  thickness?: number;
  ringColor: string;
}) {
  const total = segs.reduce((s, x) => s + x.v, 0);
  const r = size / 2 - thickness / 2;
  const C = 2 * Math.PI * r;
  let off = 0;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ transform: 'rotate(-90deg)' }}
      aria-hidden="true"
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={ringColor}
        strokeWidth={thickness}
        opacity={0.35}
      />
      {segs.map((s, i) => {
        const len = (s.v / total) * C;
        const dasharray = `${len} ${C - len}`;
        const el = (
          <circle
            key={i}
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={s.color}
            strokeWidth={thickness}
            strokeDasharray={dasharray}
            strokeDashoffset={-off}
          />
        );
        off += len;
        return el;
      })}
    </svg>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// SitePreview -abstract small site mockup for the showcase grid
// ────────────────────────────────────────────────────────────────────────────
type CaseKind = 'bakery' | 'law' | 'salon' | 'clinic' | 'restaurant' | 'boutique' | 'auto-zonas-altas' | 'mobile-mechanic' | 'car-keeprs';

export function SitePreview({ kind }: { kind: CaseKind }) {
  if (kind === 'auto-zonas-altas') {
    return (
      <div
        style={{
          background: '#092e20',
          color: '#F0EDE6',
          height: '100%',
          fontFamily: theme.fonts.body,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* nav bar */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 16px',
            borderBottom: '1px solid rgba(240,237,230,0.08)',
          }}
        >
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.2 }}>Auto Zonas Altas</span>
          <span
            style={{
              fontSize: 9,
              padding: '3px 9px',
              background: '#22C55E',
              color: '#fff',
              borderRadius: 999,
              fontWeight: 600,
              letterSpacing: 0.5,
            }}
          >
            WhatsApp
          </span>
        </div>
        {/* hero */}
        <div style={{ flex: 1, padding: '18px 16px 14px', display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', opacity: 0.45, marginBottom: 10 }}>
            Auto Zonas Altas · Funchal
          </span>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: -0.5,
              marginBottom: 10,
            }}
          >
            A oficina das
            <br />
            Zonas Altas.
          </div>
          <p style={{ fontSize: 10, opacity: 0.6, lineHeight: 1.45, margin: '0 0 14px', maxWidth: 180 }}>
            Mecânica, bate-chapa e pintura. Diagnóstico sem custo, preço por escrito.
          </p>
          <div style={{ display: 'flex', gap: 7, alignItems: 'center', marginBottom: 10 }}>
            <span
              style={{
                padding: '6px 12px',
                background: '#22C55E',
                color: '#fff',
                borderRadius: 7,
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              Marcar pelo WhatsApp
            </span>
            <span
              style={{
                padding: '6px 10px',
                border: '1px solid rgba(240,237,230,0.22)',
                borderRadius: 7,
                fontSize: 10,
                color: '#F0EDE6',
              }}
            >
              Ver preços
            </span>
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <span style={{ fontSize: 9, color: '#FBBF24' }}>★★★★★</span>
            <span style={{ fontSize: 9, opacity: 0.55 }}>4.9 (186) · Google · São Gonçalo</span>
          </div>
        </div>
        {/* stats bar */}
        <div
          style={{
            background: '#F5F0E8',
            display: 'flex',
            justifyContent: 'space-around',
            padding: '10px 12px',
          }}
        >
          {[
            { v: '18 anos', l: 'em Funchal' },
            { v: '6 200+', l: 'viaturas' },
            { v: '4,9 ★', l: '186 reviews' },
          ].map((s) => (
            <div key={s.v} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#0a1f14', letterSpacing: -0.3 }}>{s.v}</div>
              <div style={{ fontSize: 8, color: '#0a1f14', opacity: 0.5, marginTop: 1 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (kind === 'mobile-mechanic') {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src="/showcase/mobile-mechanic.png"
        alt="Madeira Mobile Mechanic website"
        style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' }}
      />
    );
  }

  if (kind === 'car-keeprs') {
    return (
      <div
        style={{
          background: '#F5F0E8',
          color: '#0F1729',
          height: '100%',
          fontFamily: theme.fonts.body,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* nav */}
        <div
          style={{
            padding: '10px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: '#fff',
            borderBottom: '1px solid #E5E0D6',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: 4,
                background: '#1B4FD8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 8,
                color: '#fff',
                fontWeight: 800,
                letterSpacing: 0.3,
              }}
            >
              CK
            </div>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: -0.2 }}>Car Keeprs Club</span>
          </div>
          <span
            style={{
              fontSize: 9,
              padding: '4px 10px',
              background: '#1B4FD8',
              color: '#fff',
              borderRadius: 999,
              fontWeight: 600,
            }}
          >
            WhatsApp us
          </span>
        </div>
        {/* hero */}
        <div style={{ flex: 1, padding: '20px 16px 0', display: 'flex', flexDirection: 'column' }}>
          <div
            style={{
              fontSize: 26,
              fontWeight: 800,
              lineHeight: 1.05,
              letterSpacing: -0.8,
              color: '#0F1729',
              marginBottom: 8,
            }}
          >
            Car Care
            <br />
            made simple
            <span style={{ color: '#E6B800' }}>.</span>
          </div>
          <p style={{ fontSize: 10, opacity: 0.6, lineHeight: 1.5, margin: '0 0 14px', maxWidth: 170 }}>
            Continuous maintenance, safety checks & service history -all in one place.
          </p>
          <div style={{ display: 'flex', gap: 7 }}>
            <span
              style={{
                padding: '6px 12px',
                background: '#1B4FD8',
                color: '#fff',
                borderRadius: 7,
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              Schedule a visit
            </span>
            <span
              style={{
                padding: '6px 10px',
                border: '1px solid #C8C0B0',
                borderRadius: 7,
                fontSize: 10,
                color: '#0F1729',
              }}
            >
              See pricing
            </span>
          </div>
        </div>
        {/* quick links bar */}
        <div
          style={{
            padding: '10px 16px',
            background: '#fff',
            borderTop: '1px solid #E5E0D6',
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap',
            marginTop: 14,
          }}
        >
          {['Services', 'Membership', 'Location', 'FAQ'].map((l) => (
            <span
              key={l}
              style={{ fontSize: 9, color: '#1B4FD8', fontWeight: 500, textDecoration: 'underline', opacity: 0.8 }}
            >
              {l}
            </span>
          ))}
        </div>
      </div>
    );
  }

  const palettes: Record<string, { bg: string; ink: string; accent: string }> = {
    bakery: { bg: '#F1E3CC', ink: '#3A2614', accent: '#C97B3D' },
    law: { bg: '#E9E5DC', ink: '#1B2233', accent: '#3B4D7A' },
    salon: { bg: '#F4E4E4', ink: '#3A1F22', accent: '#A35B6A' },
    clinic: { bg: '#E6EFE9', ink: '#0E2A21', accent: '#3F6B4A' },
    restaurant: { bg: '#161210', ink: '#F1E3CC', accent: '#D5A05A' },
    boutique: { bg: '#EFE9E0', ink: '#2B1F12', accent: '#7A6A4F' },
  };
  const p = palettes[kind] ?? { bg: '#F1E3CC', ink: '#3A2614', accent: '#C97B3D' };

  if (kind === 'restaurant') {
    return (
      <div
        style={{
          background: p.bg,
          color: p.ink,
          padding: '22px 22px 20px',
          height: '100%',
          fontFamily: theme.fonts.serif,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              fontSize: 14,
              letterSpacing: 4,
              textTransform: 'uppercase',
              fontWeight: 500,
            }}
          >
            Olivar
          </span>
          <span
            style={{
              fontSize: 10,
              opacity: 0.6,
              fontFamily: theme.fonts.body,
            }}
          >
            RESERVE · MENU · ABOUT
          </span>
        </div>
        <div>
          <div
            style={{
              fontSize: 38,
              fontStyle: 'italic',
              lineHeight: 1,
              marginBottom: 12,
            }}
          >
            A table set
            <br />
            by the sea.
          </div>
          <span
            style={{
              display: 'inline-block',
              padding: '7px 14px',
              borderRadius: 999,
              background: p.accent,
              color: p.bg,
              fontFamily: theme.fonts.body,
              fontSize: 11,
              letterSpacing: 1,
              textTransform: 'uppercase',
            }}
          >
            Reserve →
          </span>
        </div>
      </div>
    );
  }
  if (kind === 'law') {
    return (
      <div style={{ background: p.bg, color: p.ink, padding: 22, height: '100%', fontFamily: theme.fonts.body }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 11,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 28,
          }}
        >
          <span>FERREIRA &amp; ALMEIDA</span>
          <span style={{ opacity: 0.5 }}>EST. 2008</span>
        </div>
        <div
          style={{
            fontFamily: theme.fonts.serif,
            fontSize: 30,
            lineHeight: 1.05,
            fontWeight: 400,
            letterSpacing: -0.5,
            marginBottom: 14,
          }}
        >
          Counsel for the
          <br />
          matters that matter.
        </div>
        <p style={{ fontSize: 12, opacity: 0.7, lineHeight: 1.5, marginBottom: 22, margin: 0 }}>
          Civil, commercial, and family law for businesses and individuals across Portugal.
        </p>
        <div
          style={{
            borderTop: `1px solid ${p.ink}22`,
            paddingTop: 14,
            marginTop: 22,
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 10,
            opacity: 0.6,
          }}
        >
          <span>Lisboa · Porto</span>
          <span>+351 210 000 000</span>
        </div>
      </div>
    );
  }
  if (kind === 'salon') {
    return (
      <div
        style={{
          background: p.bg,
          color: p.ink,
          height: '100%',
          fontFamily: theme.fonts.body,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '14px 18px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 11,
          }}
        >
          <span style={{ fontFamily: theme.fonts.serif, fontStyle: 'italic', fontSize: 16 }}>
            Lume
          </span>
          <span style={{ opacity: 0.6 }}>BOOK</span>
        </div>
        <div
          style={{
            flex: 1,
            padding: '10px 18px 18px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
          }}
        >
          <div
            style={{
              fontFamily: theme.fonts.serif,
              fontSize: 32,
              lineHeight: 1,
              fontStyle: 'italic',
              marginBottom: 12,
            }}
          >
            Hair, color,
            <br />
            and quiet hours.
          </div>
          <div
            style={{
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
              fontSize: 10,
              opacity: 0.7,
            }}
          >
            {['Cut', 'Color', 'Balayage', 'Treatments'].map((x) => (
              <span
                key={x}
                style={{
                  padding: '4px 9px',
                  borderRadius: 999,
                  background: '#fff',
                  border: `1px solid ${p.ink}22`,
                }}
              >
                {x}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }
  if (kind === 'clinic') {
    return (
      <div style={{ background: p.bg, color: p.ink, padding: 22, height: '100%', fontFamily: theme.fonts.body }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 22 }}>
          <span
            style={{
              width: 14,
              height: 14,
              borderRadius: 999,
              background: p.accent,
              display: 'inline-block',
            }}
          />
          <span style={{ fontWeight: 600, letterSpacing: -0.2 }}>Cuida Clínica</span>
        </div>
        <div
          style={{
            fontSize: 28,
            fontWeight: 500,
            letterSpacing: -0.6,
            lineHeight: 1.05,
            marginBottom: 12,
          }}
        >
          Care that listens
          <br />
          before it prescribes.
        </div>
        <p style={{ fontSize: 12, opacity: 0.7, lineHeight: 1.5, marginBottom: 16, margin: 0 }}>
          Family medicine, nutrition, and physiotherapy under one roof.
        </p>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 12px',
            background: p.accent,
            color: '#fff',
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 500,
            marginTop: 16,
          }}
        >
          Book a visit →
        </span>
      </div>
    );
  }
  if (kind === 'boutique') {
    return (
      <div style={{ background: p.bg, color: p.ink, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '14px 18px',
            fontSize: 11,
            fontFamily: theme.fonts.body,
            letterSpacing: 2,
            textTransform: 'uppercase',
            borderBottom: `1px solid ${p.ink}22`,
          }}
        >
          <span>Atelier Roca</span>
          <span style={{ opacity: 0.6 }}>SHOP · STORY · CART (0)</span>
        </div>
        <div
          style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1,
            background: `${p.ink}15`,
          }}
        >
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                background: ['#D9C9A8', '#B89A6F', '#E5D9C0', '#A38563'][i],
                position: 'relative',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  bottom: 6,
                  left: 8,
                  fontSize: 9,
                  fontFamily: theme.fonts.body,
                  color: '#fff',
                  letterSpacing: 1,
                }}
              >
                €{[120, 89, 180, 240][i]}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // bakery default
  return (
    <div
      style={{
        background: p.bg,
        color: p.ink,
        padding: '22px 22px',
        height: '100%',
        fontFamily: theme.fonts.body,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 11,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          marginBottom: 24,
        }}
      >
        <span style={{ fontWeight: 600 }}>Maria · Padaria</span>
        <span style={{ opacity: 0.5 }}>since 1962</span>
      </div>
      <div
        style={{
          fontFamily: theme.fonts.serif,
          fontSize: 36,
          lineHeight: 1,
          fontStyle: 'italic',
          marginBottom: 14,
        }}
      >
        Pão fresco,
        <br />
        todos os dias.
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18, fontSize: 11, opacity: 0.7 }}>
        <span>Open 07:00</span>
        <span>·</span>
        <span>Rua das Flores 12</span>
      </div>
      <div style={{ marginTop: 'auto', display: 'flex', gap: 6, alignItems: 'center', fontSize: 11 }}>
        <span style={{ padding: '7px 12px', background: p.accent, color: p.bg, borderRadius: 8 }}>
          Order online
        </span>
        <span style={{ padding: '7px 12px', border: `1px solid ${p.ink}33`, borderRadius: 8 }}>Menu</span>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Container -page-width wrapper used by every section
// ────────────────────────────────────────────────────────────────────────────
export function Container({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div
      className="lp-container"
      style={{
        maxWidth: 1280,
        margin: '0 auto',
        padding: '0 32px',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Pill / Chip -small label
// ────────────────────────────────────────────────────────────────────────────
export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-block',
        fontFamily: theme.fonts.mono,
        fontSize: 12,
        letterSpacing: 1.2,
        color: theme.c.accent,
        textTransform: 'lowercase',
        marginBottom: 16,
      }}
    >
      {children}
    </span>
  );
}
